import React, { useMemo, useState } from 'react';
import { describeLoadError, formatDateTime, plural, timeAgo, toDate } from './utils/visitorStatsHelpers';
import { ANON, UserBadge } from './VisitorStatsParts';
import './VisitorStatsTabs.css';

const PAGE_SIZE = 30;

const normalise = (event) => {
  const by = event.generatedBy;
  return {
    id: event.id,
    date: toDate(event.timestamp),
    type: event.type === 'download' ? 'download' : 'generate',
    dancer: event.dancerName || 'Unknown dancer',
    dances: Array.isArray(event.dances) ? event.dances : [],
    user: by && (by.email || by.name) ? { key: by.email || by.userId || by.name, name: by.name || by.email, email: by.email || null } : ANON,
  };
};

const TypeBadge = ({ type }) => (
  type === 'download'
    ? <span className="vs-badge vs-badge--info">⬇️ Downloaded</span>
    : <span className="vs-badge vs-badge--ok">🖼️ Generated</span>
);

function ShareEventCard({ event, showUser = true }) {
  return (
    <article className="vs-event">
      <header className="vs-event-head">
        {showUser ? <UserBadge user={event.user} /> : <span className="vs-dancer">💃 {event.dancer}</span>}
        <time className="vs-event-time" title={formatDateTime(event.date)}>
          {formatDateTime(event.date)}
          <span>{timeAgo(event.date)}</span>
        </time>
      </header>
      <div className="vs-vs-row">
        {showUser && <span className="vs-dancer">💃 {event.dancer}</span>}
        <TypeBadge type={event.type} />
      </div>
      {event.dances.length > 0 && (
        <div className="vs-tags">
          {event.dances.map((d, i) => <span key={i} className="vs-tag">{d}</span>)}
        </div>
      )}
    </article>
  );
}

function GeneratorRow({ entry }) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(8);
  const dancers = [...new Set(entry.events.map((e) => e.dancer))];
  return (
    <li className={`vs-gen ${open ? 'is-open' : ''}`}>
      <button type="button" className="vs-gen-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <UserBadge user={entry.user} />
        <span className="vs-chips">
          <span className="vs-chip">🖼️ {plural(entry.generated, 'card')} generated</span>
          {entry.downloaded > 0 && <span className="vs-chip">⬇️ {entry.downloaded} downloaded</span>}
          <span className="vs-chip">🕐 {timeAgo(entry.last)}</span>
        </span>
        <svg className="vs-chev" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
          <path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="vs-gen-body">
          <p className="vs-user-dates">Cards made for: </p>
          <div className="vs-tags">{dancers.map((d) => <span key={d} className="vs-tag">💃 {d}</span>)}</div>
          <div className="vs-event-list vs-event-list--tight">
            {entry.events.slice(0, shown).map((e) => <ShareEventCard key={e.id} event={e} showUser={false} />)}
          </div>
          {shown < entry.events.length && (
            <button type="button" className="vs-more" onClick={() => setShown(shown + 20)}>
              Show more <span>({entry.events.length - shown} hidden)</span>
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export default function VisitorShareTab({ events, loading, error, onRetry }) {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [order, setOrder] = useState('newest');
  const [shown, setShown] = useState(PAGE_SIZE);

  const all = useMemo(() => events.map(normalise), [events]);

  const summary = useMemo(() => {
    const generators = {};
    const dancers = {};
    let generated = 0;
    let downloaded = 0;
    let anonymous = 0;
    all.forEach((e) => {
      if (e.type === 'generate') generated += 1; else downloaded += 1;
      if (e.user.key === ANON.key) anonymous += 1;
      const g = generators[e.user.key] || (generators[e.user.key] = { user: e.user, generated: 0, downloaded: 0, events: [], last: e.date });
      if (e.type === 'generate') g.generated += 1; else g.downloaded += 1;
      g.events.push(e);
      if ((e.date?.getTime() || 0) > (g.last?.getTime() || 0)) g.last = e.date;
      const d = dancers[e.dancer] || (dancers[e.dancer] = { name: e.dancer, count: 0, users: new Set() });
      d.count += 1;
      d.users.add(e.user.key);
    });
    const people = Object.values(generators);
    people.forEach((g) => g.events.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0)));
    return {
      generated,
      downloaded,
      anonymous,
      dancerCount: Object.keys(dancers).length,
      signedInUsers: people.filter((g) => g.user.key !== ANON.key).length,
      // signed-in users first (by activity), anonymous last
      generators: people.sort((a, b) =>
        (a.user.key === ANON.key) - (b.user.key === ANON.key) || (b.generated + b.downloaded) - (a.generated + a.downloaded)),
      topDancers: Object.values(dancers).sort((a, b) => b.count - a.count).slice(0, 10),
    };
  }, [all]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = all.filter((e) => {
      if (type !== 'all' && e.type !== type) return false;
      return !q || `${e.user.name} ${e.user.email || ''} ${e.dancer} ${e.dances.join(' ')}`.toLowerCase().includes(q);
    });
    filtered.sort((a, b) => ((a.date?.getTime() || 0) - (b.date?.getTime() || 0)) * (order === 'newest' ? -1 : 1));
    return filtered;
  }, [all, search, type, order]);

  if (loading) return <div className="stats-section vs-tab"><div className="loading-message">Loading share cards…</div></div>;

  if (error) {
    return (
      <div className="stats-section vs-tab">
        <h2>🖼️ Share card analytics</h2>
        <div className="vs-alert" role="alert">
          <p>{describeLoadError(error, 'shareCardEvents')}</p>
          <button type="button" className="vs-btn" onClick={onRetry}>Retry</button>
        </div>
      </div>
    );
  }

  if (all.length === 0) {
    return (
      <div className="stats-section vs-tab">
        <h2>🖼️ Share card analytics</h2>
        <div className="vs-empty-box">
          No share cards recorded in this period. They appear here when someone generates or downloads a result card from a dancer profile.
        </div>
      </div>
    );
  }

  return (
    <div className="vs-tab">
      <div className="stats-section">
        <h2>🖼️ Share card analytics</h2>
        <div className="vs-kpis">
          <div className="vs-kpi"><b>{summary.generated.toLocaleString()}</b><span>Cards generated</span></div>
          <div className="vs-kpi"><b>{summary.downloaded.toLocaleString()}</b><span>Images downloaded</span></div>
          <div className="vs-kpi"><b>{summary.signedInUsers.toLocaleString()}</b><span>Signed-in users</span></div>
          <div className="vs-kpi"><b>{summary.dancerCount.toLocaleString()}</b><span>Dancers shared</span></div>
          <div className="vs-kpi"><b>{summary.anonymous.toLocaleString()}</b><span>Anonymous actions</span></div>
        </div>
      </div>

      <div className="stats-section">
        <h2>👤 Who generated share cards</h2>
        <p className="vs-note">Tap a user to see every card they made.</p>
        <ul className="vs-gen-list">
          {summary.generators.map((g) => <GeneratorRow key={g.user.key} entry={g} />)}
        </ul>
      </div>

      <div className="stats-section">
        <h2>💃 Most shared dancers</h2>
        <ol className="vs-rank">
          {summary.topDancers.map((d, i) => (
            <li key={d.name}>
              <span className="vs-rank-n">{i + 1}</span>
              <button type="button" className="vs-rank-main" onClick={() => { setSearch(d.name); setShown(PAGE_SIZE); document.getElementById('vs-all-share')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                <span className="vs-rank-name">{d.name}</span>
                <span className="vs-rank-sub">{plural(d.users.size, 'user')}</span>
              </button>
              <span className="vs-rank-count">{d.count}×</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="stats-section" id="vs-all-share">
        <h2>🗒️ All share activity ({list.length.toLocaleString()})</h2>
        <div className="vs-toolbar">
          <input
            type="search"
            className="vs-input"
            placeholder="Search user, dancer or dance…"
            aria-label="Search share activity"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShown(PAGE_SIZE); }}
          />
          <select className="vs-input vs-select" aria-label="Filter by action" value={type} onChange={(e) => { setType(e.target.value); setShown(PAGE_SIZE); }}>
            <option value="all">Generated + downloaded</option>
            <option value="generate">Generated only</option>
            <option value="download">Downloaded only</option>
          </select>
          <select className="vs-input vs-select" aria-label="Sort share activity" value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>
        <div className="vs-event-list">
          {list.slice(0, shown).map((e) => <ShareEventCard key={e.id} event={e} />)}
          {list.length === 0 && <div className="vs-empty-box">Nothing matches these filters.</div>}
        </div>
        {shown < list.length && (
          <button type="button" className="vs-more" onClick={() => setShown(shown + PAGE_SIZE)}>
            Show {Math.min(PAGE_SIZE, list.length - shown)} more <span>({list.length - shown} hidden)</span>
          </button>
        )}
      </div>
    </div>
  );
}
